import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Icon,
    makeStyles,
    useMediaQuery,
    useTheme,
} from '@material-ui/core';
import type { Theme } from '@material-ui/core';

import { useAppParams } from '@klp/core/app/useAppParams';
import { Block, Heading } from '@klp/design';
import { ActionButton } from '@klp/design/ActionButton';
import { FileDownload } from '@klp/filedownload/views';

import { messageApiRoutes } from '../api';
import { Message, MessageStatus } from '../classes';
import { MessageBoardRoutes } from '../config';
import { useUpdateMessageStatusMutation } from '../rtkApi';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        backgroundColor: theme.palette.primary.veryLight,
        borderBottom: '1px solid transparent',
        borderBottomColor: theme.palette.primary.medium,
        borderTop: '1px solid transparent',
        borderTopColor: theme.palette.primary.medium,
    },
    buttonGroup: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignContent: 'center',
        alignItems: 'center',
        width: '100%',
        flexWrap: 'wrap' as const,
        flexDirection: 'row' as const,
    },
    button: {
        margin: theme.spacing(1),
    },
    iconLeft: {
        marginRight: theme.spacing(1),
    },
    iconRight: {
        marginLeft: theme.spacing(1),
    },
}));

interface IProps {
    message: Message;
}

export const MessageCardActions: React.FC<IProps> = ({ message }) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
    const history = useHistory();
    const { selectedClient } = useAppParams();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState<MessageStatus>(
        MessageStatus.Draft,
    );
    const [isLoading, setIsLoading] = useState(false);

    const [updateMessageStatus, { isSuccess }] =
        useUpdateMessageStatusMutation();

    // Close dialog when message status changes
    useEffect(() => {
        if (isSuccess) {
            setDialogOpen(false);
            setIsLoading(false);
        }
    }, [isSuccess]);

    const handleClose = () => {
        if (!isLoading) {
            setDialogOpen(false);
        }
    };

    const editMessage = (id: number) => {
        history.push(MessageBoardRoutes.edit(id));
    };

    const showDialog = (type: MessageStatus) => () => {
        setDialogOpen(true);
        setDialogType(type);
    };

    const dispatchAction = (status: MessageStatus) => async () => {
        const { id } = message;
        if (id) {
            setIsLoading(true);
            try {
                await updateMessageStatus({
                    id,
                    status,
                    kundeNummer: selectedClient?.kundeNummer || '',
                }).unwrap();
            } catch (error) {
                // Error handling is done by RTK Query
                setIsLoading(false);
            }
        }
    };

    if (!message) {
        return null;
    }

    const { id, status } = message;

    return (
        <>
            <Dialog
                fullScreen={fullScreen}
                fullWidth={true}
                maxWidth="xs"
                open={dialogOpen}
            >
                <DialogTitle
                    id="responsive-dialog-title"
                    disableTypography={true}
                >
                    <Heading
                        translation={`message.dialog.${dialogType}.title`}
                    />
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t(`message.dialog.${dialogType}.description`)}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <ActionButton
                        actionType="cancel"
                        translation="general.cancel"
                        onClick={handleClose}
                        disabled={isLoading}
                    />
                    <ActionButton
                        actionType="forward"
                        translation={`message.dialog.${dialogType}.action`}
                        onClick={dispatchAction(dialogType)}
                        isLoading={isLoading}
                    />
                </DialogActions>
            </Dialog>

            <Block
                className={classes.container}
                marginBottom="compact"
                paddingVertical="tiny"
            >
                <Grid container={true}>
                    <Grid item={true} xs={true} className={classes.buttonGroup}>
                        <Button
                            className={classes.button}
                            variant="outlined"
                            color="primary"
                            onClick={() => editMessage(id ?? 0)}
                        >
                            <Icon>edit</Icon>
                            {t('general.edit')}
                        </Button>
                        {[
                            MessageStatus.Draft,
                            MessageStatus.Published,
                            MessageStatus.Expired,
                            MessageStatus.Deleted,
                        ].map((messageStatus) => (
                            <Button
                                key={messageStatus}
                                className={classes.button}
                                variant="outlined"
                                onClick={showDialog(messageStatus)}
                                disabled={messageStatus === status}
                                startIcon={Message.icon(messageStatus)}
                            >
                                {t(`message.actions.${messageStatus}`)}
                            </Button>
                        ))}
                        <Button
                            className={classes.button}
                            variant="outlined"
                            startIcon={<Icon>print</Icon>}
                        >
                            <FileDownload
                                path={messageApiRoutes.messagePreview(
                                    message.id,
                                )}
                                name={`${message.title}.pdf`}
                            >
                                <>{t('message.download')}</>
                            </FileDownload>
                        </Button>
                    </Grid>
                </Grid>
            </Block>
        </>
    );
};
